import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { BaseUrl } from './baseurl';
import { CryptoHelper, EncryptionKey } from './crypto-helper';
import { SelectedPaymentTokens, StringHelper } from './strings-helper';
import { TransactionData } from './api-request';

/**
 * Created by Stephen on 17/09/2022.
 */
@Injectable({
  providedIn: 'root',
})
export class AppService extends BaseUrl {
  webEngineBaseUrl = '/api/v1/web-engine/process';
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  };
  private subject = new BehaviorSubject<any>(null);
  private requestCompleteSubject = new Subject<void>();
  private paymentMethodSort = [
    { channelName: 'CARD', isActive: true },
    { channelName: 'TRANSFER', isActive: false },
    { channelName: 'USSD', isActive: false },
    { channelName: 'QR', isActive: false },
    { channelName: 'APPLE_PAY', isActive: false },
    { channelName: 'GOOGLE_PAY', isActive: false },
    { channelName: 'AT_EASE', isActive: false },
    { channelName: 'PAYATTITUDE', isActive: false },
    { channelName: 'OPAY', isActive: false },
  ];
  transactionData!: TransactionData;
  customTermUrl: string = `${this.BASE_URL}/api/v1/web-engine/process/cardinal-callback`;
  thirdPartyBaseUrl = 'https://api.countrystatecity.in/v1';
  generateBrowserIpUrl = 'https://api64.ipify.org?format=json';

  constructor(private http: HttpClient) {
    super();
  }

  private buildRequestHeaders(): void {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    headers = headers.set('Api-Public-Key', this.processData().key);

    this.httpOptions.headers = headers;
  }

  private buildExternalRequestHeader(): void {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    this.httpOptions.headers = headers;
  }

  // SDK CLIENT URL AND HASH TRACKER
  logSDKHash(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/sdk-log';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  //PAYMENT INITIALIZATION

  initializeTransaction(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url =
      this.BASE_URL + this.webEngineBaseUrl + '/transaction-initialization';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  //PROFILE REFERENCE
  profileUser(request: any): Observable<any> {
    request = StringHelper.toUrlString(request);
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + `/profiling?${request}`;
    return this.http.post<any>(url, null, this.httpOptions);
  }

  //CARD TRANSACTIONS

  getBrowserIP(): Observable<any> {
    const url = `${this.generateBrowserIpUrl}`;
    return this.http.get<any>(url);
  }

  initiateCardPayment(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/card-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  authenticateCardPayment(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/card-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  enrollPayer(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/card-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  confirmCardPayment(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/card-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  getCardTransactionFee(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/card-transaction-fee';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  fetchSavedCards(request: any): Observable<any> {
    this.buildRequestHeaders();
    request = StringHelper.toUrlString(request);
    const url =
      this.BASE_URL +
      this.webEngineBaseUrl +
      `/fetch-customer-cards?${request}`;
    return this.http.get<any>(url, this.httpOptions);
  }

  getSdkPublicKey(): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/sdk-public-key';
    return this.http.get<any>(url, this.httpOptions);
  }

  // The public key is the same for every transaction and the endpoint is rate
  // limited, so the imported key is cached for the life of the popup rather
  // than per reference. Caching the promise also shares one in-flight fetch
  // between concurrent callers.
  private encryptionKeyPromise: Promise<EncryptionKey> | null = null;

  getEncryptionKey(): Promise<EncryptionKey> {
    if (!this.encryptionKeyPromise) {
      this.encryptionKeyPromise = this.fetchEncryptionKey().catch((err) => {
        // Never cache a failure, or a single 429 would poison the session.
        this.encryptionKeyPromise = null;
        throw err;
      });
    }
    return this.encryptionKeyPromise;
  }

  // Called when a card request fails so a rotated server key self-heals on
  // the user's retry instead of failing for the life of the popup.
  invalidateEncryptionKey() {
    this.encryptionKeyPromise = null;
  }

  private async fetchEncryptionKey(): Promise<EncryptionKey> {
    const res = await firstValueFrom(this.getSdkPublicKey());
    if (!res?.success || !res.data) {
      throw new Error('Unable to retrieve the SDK public key');
    }
    // The endpoint currently returns the JWK as a JSON string; accept an object too.
    const jwk = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
    return CryptoHelper.importPublicKey(jwk);
  }

  confirmTokenizedCardPayment(request: any): Observable<any> {
    // this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/card-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  //USSD TRANSACTIONS

  private ussdState: {
    reference: string;
    bankUssdCodes: any;
    selectedBank: any | null;
    expiresAt: number | null;
  } | null = null;

  setUssdState(state: {
    reference: string;
    bankUssdCodes: any;
    selectedBank: any | null;
    expiresAt: number | null;
  }) {
    this.ussdState = state;
  }

  getUssdState(reference: string) {
    if (this.ussdState?.reference !== reference) return null;
    return this.ussdState;
  }

  clearUssdState() {
    this.ussdState = null;
  }

  generateUssdCode(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/ussd-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  queryUssdTransaction(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/ussd-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  queryTransactions(eventType: string, reference: string): Observable<any> {
    this.buildRequestHeaders();
    const url = `${
      this.BASE_URL + this.webEngineBaseUrl
    }/${eventType}-status?transactionRef=${reference}`;
    return this.http.get<any>(url, this.httpOptions);
  }

  //VIRTUAL ACCOUNT TRANSACTIONS

  private virtualAccountState: {
    reference: string;
    responseData: any;
    expiresAt: number;
  } | null = null;

  setVirtualAccountState(state: {
    reference: string;
    responseData: any;
    expiresAt: number;
  }) {
    this.virtualAccountState = state;
  }

  getVirtualAccountState(reference: string) {
    if (this.virtualAccountState?.reference !== reference) return null;
    if (Date.now() >= this.virtualAccountState.expiresAt) return null;
    return this.virtualAccountState;
  }

  clearVirtualAccountState() {
    this.virtualAccountState = null;
  }

  // CARD FEE (BIN LOOKUP) STATE

  private cardFeeState: {
    reference: string;
    international: boolean;
    feeMinor: number;
  } | null = null;

  setCardFeeState(state: {
    reference: string;
    international: boolean;
    feeMinor: number;
  }) {
    this.cardFeeState = state;
  }

  getCardFeeState(reference: string) {
    if (this.cardFeeState?.reference !== reference) return null;
    return this.cardFeeState;
  }

  clearCardFeeState() {
    this.cardFeeState = null;
  }

  generateVirtualAccount(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url =
      this.BASE_URL + this.webEngineBaseUrl + '/virtual-account-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  transferPaymentStatus(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url =
      this.BASE_URL + this.webEngineBaseUrl + '/virtual-account-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  //QR TRANSACTIONS

  generateQRCode(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/qr-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  queryQRransaction(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/qr-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  //APPLE PAY TRANSACTIONS
  verifyMerchant(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/apple/create-session';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  // PAYATTITUDE TRANSACTIONS
  payattitudeRequest(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/payattitude-request';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  // PAYWITHOPAY TRANSACTIONS
  paywithopayRequest(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = this.BASE_URL + this.webEngineBaseUrl + '/opay';
    return this.http.post<any>(url, request, this.httpOptions);
  }

  confirmOpayPayment(request: any): Observable<any> {
    this.buildRequestHeaders();
    const url = `${this.BASE_URL}${this.webEngineBaseUrl}/verify-opay-transaction?reference=${request.transactionRef}`;
    return this.http.get(url, this.httpOptions);
  }

  //MISC

  sendData(data: any) {
    this.subject.next(data);
  }

  getData(): Observable<any> {
    return this.subject.asObservable();
  }

  sortPaymentChannels(channels: string[]) {
    // Creating an object from the selectedMerchantToken received from backend
    let channelsObj = channels.map((channel: string) => ({
      channelName: channel,
      isActive: false,
    }));

    // Sorting the tokens to match design
    return this.paymentMethodSort
      .map((channel: SelectedPaymentTokens) =>
        channelsObj.find(
          (method: SelectedPaymentTokens) =>
            channel.channelName == method.channelName,
        ),
      )
      .filter((item): item is SelectedPaymentTokens => Boolean(item));
  }

  applyFeatureFlags(
    channels: SelectedPaymentTokens[],
    availableChannels: string[],
  ): SelectedPaymentTokens[] {
    return channels.filter((ch) =>
      (availableChannels ?? []).includes(ch.channelName),
    );
  }

  getDefaultChannelsFromFeatureFlags(
    availableChannels: string[],
  ): SelectedPaymentTokens[] {
    return (availableChannels ?? []).map((channelName) => ({
      channelName,
      isActive: false,
    }));
  }

  modifyPaymentTokenName(token: string) {
    if (token == 'USSD' || token == 'QR') return token;
    else if (token == 'AT_EASE') {
      return '@Ease Wallet';
    } else {
      return token
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }

  processData() {
    // Process the received data
    return this.transactionData;
  }

  getRequestCompleteObservable() {
    return this.requestCompleteSubject.asObservable();
  }

  signalRequestComplete() {
    this.requestCompleteSubject.next();
  }

  getCountries(): Observable<any> {
    this.buildExternalRequestHeader();
    const url = `${this.thirdPartyBaseUrl}/countries`;
    return this.http.get(url, this.httpOptions);
  }

  getStates(countryIso: string): Observable<any> {
    this.buildExternalRequestHeader();
    const url = `${this.thirdPartyBaseUrl}/countries/${countryIso}/states`;
    return this.http.get(url, this.httpOptions);
  }
}
